import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ShieldCheck, Lock, FileText, Headphones } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CheckoutButton from "@/components/CheckoutButton";
import { API_BASE } from "@/lib/track";

export const DigitalProducts = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/products`)
      .then(({ data }) => setProducts(data.products || []))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section id="products" className="relative py-24 sm:py-32 bg-obsidian-900/50 border-y border-slate-800/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Self-Serve · Digital Store</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-4">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                Digital Products
              </h2>
              <p className="text-slate-400 mt-4 max-w-2xl leading-relaxed">
                Not every business needs a full-service engagement. These practical tools are
                delivered digitally — purchase, and your product is prepared and opened right away.
              </p>
            </div>
            <div className="rounded-2xl border border-gold/30 bg-obsidian-800/70 px-6 py-4 gold-ring" data-testid="products-starting-at">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Pricing</p>
              <p className="font-display text-3xl font-extrabold text-white mt-1">
                Starting at <span className="text-gold-gradient">$7</span>
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="product-grid">
          {products.length === 0 && (
            <div className="col-span-full rounded-2xl border border-slate-800 bg-obsidian-800/50 p-10 text-center text-slate-500" data-testid="products-loading">
              Loading the store…
            </div>
          )}
          {products.map((p, i) => (
            <Reveal key={p.sku} delay={Math.min(i * 0.05, 0.3)} className="h-full">
              <div
                data-testid={`product-card-${p.sku.replace("aih_prod_", "")}`}
                className="group h-full rounded-3xl border border-slate-700/60 bg-obsidian-800/60 hover:border-gold/40 p-6 flex flex-col transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">
                    <BookOpen className="w-3 h-3" /> {p.type}
                  </span>
                  <span className="font-display text-xl font-extrabold text-gold">
                    ${(p.price / 100).toFixed(0)}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-white mt-4 leading-snug">{p.name}</h3>
                <p className="text-slate-400 text-xs leading-relaxed mt-3 flex-1">{p.description}</p>
                <CheckoutButton
                  lookup={p.sku}
                  name={p.name}
                  price={`$${(p.price / 100).toFixed(2)}`}
                  label={`Get It — $${(p.price / 100).toFixed(0)}`}
                  testid={`product-buy-${p.sku.replace("aih_prod_", "")}`}
                  className="mt-5 w-full rounded-full border border-gold/60 text-gold font-display font-bold py-3 text-sm hover:bg-gold hover:text-obsidian transition-colors duration-300"
                />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10">
          <div className="rounded-2xl border border-gold/25 bg-obsidian-800/50 p-6 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
            <div className="flex gap-4">
              <ShieldCheck className="w-6 h-6 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300 leading-relaxed max-w-xl">
                <span className="text-gold font-semibold">7-Day Guarantee.</span> Eligible purchases
                are covered by our 7-day money-back guarantee, subject to our{" "}
                <Link to="/refund-policy" data-testid="products-refund-link" className="text-gold underline underline-offset-2">
                  Refund Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400 shrink-0">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-emerald-400" /> Secure Checkout</span>
              <span className="inline-flex items-center gap-1.5"><FileText className="w-3 h-3 text-emerald-400" /> Clear Scope</span>
              <span className="inline-flex items-center gap-1.5"><Headphones className="w-3 h-3 text-emerald-400" /> Human Support</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default DigitalProducts;
